import {
  AnalyticsActionBuyEvent,
  AnalyticsEventParam,
  PaymentResult,
  ReceiptStatus,
} from '@accedo/one-plugin-sdk';
import { t } from 'i18next';
import { FC, useCallback, useEffect, useState } from 'react';

import { Payment, PurchaseParams } from '#/components/Payment';
import tempPromoStore from '#/components/PlanPurchaseFlow/tempPromoStore';
import { PaymentStore } from '#/plugins/paywall/models';
import { noop } from '#/service/utils';
import { authActions } from '#/store/auth';
import { generalModalActions } from '#/store/modals';
import { usePaywall } from '#/store/paywall';

import { PurchasePlanStepProps } from '../../types';
import { Discount } from './Discount';
import { SelectedPlan } from './SelectedPlan';
import * as Styled from './styled';

const PurchasePlanStep: FC<PurchasePlanStepProps> = (props) => {
  const {
    actions: { handleClose },
    previousStep,
    selectedPlan,
    customParameters,
    onPaymentChange,
  } = props;
  const [providerName, setProviderName] = useState<string | null>();
  const [price, setPrice] = useState(selectedPlan?.price ?? '0');
  const [voucher, setVoucher] = useState<string>();
  const [isCardRequired, setIsCardRequired] = useState<boolean>(true);
  const availablePayments = selectedPlan?.paymentMethods ?? [];
  const { api: paywallApi } = usePaywall();

  const hasOwnUi = paywallApi.hasOwnUi();

  useEffect(() => {
    paywallApi.getProviderInfo().then(({ name }) => {
      setProviderName(name);
    });
  }, [paywallApi]);

  const sendBuyEvent = useCallback(
    ({ amountCharged, currency, transactionId }: PaymentResult) => {
      // #ExternalAnalytics - Buy
      document.dispatchEvent(
        new AnalyticsActionBuyEvent({
          [AnalyticsEventParam.TransactionId]: transactionId,
          [AnalyticsEventParam.Price]: amountCharged,
          [AnalyticsEventParam.Store]: providerName ?? PaymentStore.Unknown,
          [AnalyticsEventParam.Currency]: currency,
          [AnalyticsEventParam.ReceiptStatus]: ReceiptStatus.Successful,
          [AnalyticsEventParam.SubscriptionPlan]: selectedPlan?.title,
        })
      );
    },
    [selectedPlan?.title, providerName]
  );

  const onPurchase = useCallback(
    async ({ card, paymentMethod }: PurchaseParams) => {
      if (!paywallApi.purchase) {
        throw new Error('No paywallApi.purchase');
      }
      const planId = selectedPlan?.id ?? '';

      // #promo-campaigns This is required by M&Ms promo campaign. When making a purchase, we need to store the promo
      // details in a session storage, then retrieve them when finalizing the purchase. Session Storage isn't the best
      // solution, but it's straightforward, works, and doesn't affect other places in the application.
      const promoDetails = tempPromoStore.read() ?? {};
      const params = {
        ...customParameters,
        ...(promoDetails.customSubscriptionParameters ?? {}),
      };

      try {
        const res = await paywallApi.purchase(planId, {
          card,
          paymentMethod,
          voucherCode: voucher,
          customParameters: params,
          startDate: promoDetails.startDate,
          customOrderParameters: promoDetails.customOrderParameters,
        });

        // `res` here contains the actual price paid, order id, renewal date, etc.
        console.debug(
          'Plan purchase successful, potentially finalized externally.',
          res
        );
        sendBuyEvent(res);
        tempPromoStore.remove();

        // After purchase, we need to refresh profile data, so that the user will be able to consume the content
        // right away. It does mean that the spinner will be significantly longer, because we will wait for
        // the successful resolution (or up to X seconds as defined in the action below)
        await authActions.refreshProfileAfterEntitlementPurchase();

        onPaymentChange({ isSuccessful: true, isExternal: res.isExternal });

        return res;
      } catch (err) {
        console.error('Plan purchase failed', err);

        throw err;
      }
    },
    [
      paywallApi,
      selectedPlan?.id,
      customParameters,
      voucher,
      sendBuyEvent,
      onPaymentChange,
    ]
  );

  const onFailure = useCallback(
    (err) => {
      console.error('Payment error', err);
      generalModalActions.showModal({
        title: t('iap.error.title'),
        message: t('iap.error.message'),
        // the drop-in form doesn't support retrying in place
        onDismiss: hasOwnUi ? handleClose : undefined,
      });
    },
    [handleClose, hasOwnUi]
  );

  const onVoucherChange = useCallback(
    async (planId, voucher) => {
      if (!paywallApi.validateVoucher) {
        throw new Error('validateVoucher is not implemented');
      }

      return paywallApi
        .validateVoucher(voucher, planId)
        .then((res) => {
          setVoucher(voucher);
          setIsCardRequired(res.isPaymentDetailsRequired);

          return res;
        })
        .catch((err) => {
          setVoucher(undefined);
          throw err;
        });
    },
    [paywallApi]
  );

  if (!selectedPlan) {
    console.warn('No selected plan was found.');

    return null;
  }

  return (
    <>
      <Styled.Receipt>
        <SelectedPlan
          {...selectedPlan}
          onPlanChange={previousStep}
          price={price}
        />
        <Styled.ReceiptLineBreak />
        <Discount
          {...selectedPlan}
          onPriceChange={setPrice}
          onVoucherChange={onVoucherChange}
          price={price}
        />
      </Styled.Receipt>

      <Payment
        buyButtonText="web.iap.subscribe.button"
        hasOwnUi={hasOwnUi}
        isCardRequired={isCardRequired}
        onFailure={onFailure}
        onPurchase={onPurchase}
        onSuccess={noop}
        paymentMethods={availablePayments}
      />

      <Styled.PaymentFormFooter linkTarget="_blank">
        {t('web.iap.paymentFooter')}
      </Styled.PaymentFormFooter>
    </>
  );
};

export default PurchasePlanStep;
